import { useCallback, useEffect, useState } from 'react';
import { getPublicIntake, submitPublicIntake } from '../core/api';
import { TurnstileWidget } from '../auth/TurnstileWidget';
import { Field, Shell, Unavailable } from './PublicFormPage';
import { publicPayload, visibleFields } from './publicFormLogic';

/*
 * The hosted public form — docs/public-intake-scoping-2026-10.md. Reached at
 * `/pi/<key>`, and designed to be embedded in an iframe on a customer's own
 * website (it is the one path the platform lets itself be framed on).
 *
 * What is deliberate here:
 *
 * 1. **It names no org and no app.** The visitor is on the customer's own
 *    website already; the page's job is the form and nothing else, and it
 *    cannot learn anything about the tenant because the server never sends it.
 *
 * 2. **It renders before the auth gate** and sends no cookie, for the reasons
 *    `PublicFormPage.jsx` lists. A visitor who also has a session here must not
 *    have it ride along.
 *
 * 3. **The CAPTCHA is not optional.** The server refuses to run an intake with
 *    no provider configured, so a form that loads has a site key; the widget
 *    token goes with the submit and a missing one is refused server-side.
 *
 * 4. **A honeypot and a signed timer.** `hp_website` is a field a person never
 *    sees and a bot fills in; `form_token` is the server's signed timestamp, so
 *    "how long was this form open" cannot be forged. Both are answered with a
 *    normal-looking success so a bot is not told to try again.
 *
 * 5. **One sentence for every "no".** A failed load shows the server's own
 *    generic message verbatim and does not try to explain why.
 *
 * 6. **The success message is the customer's own** (`success_message`), and says
 *    nothing about whether the submission is reviewed, kept, or recognised as
 *    a repeat.
 */
export function PublicIntakePage({ intakeKey }) {
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [values, setValues] = useState({});
  const [consent, setConsent] = useState(false);
  const [captcha, setCaptcha] = useState('');
  const [hp, setHp] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    let live = true;
    getPublicIntake(intakeKey)
      .then((f) => { if (live) setForm(f); })
      .catch((e) => { if (live) setError(e.message || 'This form is not available right now.'); });
    return () => { live = false; };
  }, [intakeKey]);

  const set = useCallback((k, v) => setValues((p) => ({ ...p, [k]: v })), []);
  const onToken = useCallback((t) => setCaptcha(t), []);

  const submit = async () => {
    setBusy(true);
    setSubmitError(null);
    try {
      const out = await submitPublicIntake(intakeKey, {
        values: publicPayload(form.fields, values),
        consent: consent || undefined,
        captcha_token: captcha,
        form_token: form.form_token,
        [form.honeypot]: hp,
      });
      setDone(out.message || 'Thank you.');
    } catch (e) {
      // A reload mints a fresh signed timer, which is what an expired one needs.
      if (e.body && e.body.error === 'form_expired') {
        try { setForm(await getPublicIntake(intakeKey)); } catch { /* keep the message */ }
      }
      setSubmitError(e.message || "That didn't send — mind trying again?");
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Unavailable message={error} />;
  if (!form) {
    return (
      <Shell>
        <p className="text-center text-sm text-pencil dark:text-sand">Loading…</p>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="rounded-2xl border border-sand dark:border-espresso-800 bg-paper dark:bg-espresso-900 p-6 text-center">
          <h1 className="font-display text-xl text-ink dark:text-paper">Thank you</h1>
          <p className="mt-2 text-sm text-pencil dark:text-sand">{done}</p>
        </div>
      </Shell>
    );
  }

  const needsCaptcha = !!form.captcha?.site_key;
  const canSend = !busy && (!needsCaptcha || !!captcha) && (!form.consent?.required || consent);

  return (
    <Shell>
      <div className="rounded-2xl border border-sand dark:border-espresso-800 bg-paper dark:bg-espresso-900 p-5 md:p-6">
        <h1 className="font-display text-lg text-ink dark:text-paper">{form.title}</h1>

        <div className="mt-5 space-y-4">
          {visibleFields(form.fields, values).map((spec) => (
            <Field key={spec.key} spec={spec} value={values[spec.key]} onChange={set} disabled={busy} />
          ))}

          {/* The honeypot: visually hidden, out of the tab order, ignored by
              password managers. A person never reaches it. */}
          <div aria-hidden="true"
               style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
            <label htmlFor="f_hp">Leave this empty</label>
            <input id="f_hp" tabIndex={-1} autoComplete="off" value={hp}
                   onChange={(e) => setHp(e.target.value)} />
          </div>

          {form.consent?.text && (
            <label className="flex items-start gap-2 text-sm text-ink dark:text-paper">
              <input type="checkbox" checked={consent} disabled={busy}
                     onChange={(e) => setConsent(e.target.checked)} className="mt-1" />
              <span>
                {form.consent.text}
                {form.consent.required && <span className="text-rose-500 ml-0.5" aria-hidden="true">*</span>}
              </span>
            </label>
          )}

          <TurnstileWidget siteKey={form.captcha?.site_key} onToken={onToken} />
        </div>

        {submitError && (
          <p role="alert" className="mt-4 text-sm text-rose-600 dark:text-rose-400">{submitError}</p>
        )}

        <button onClick={submit} disabled={!canSend}
                className="mt-5 w-full rounded-lg bg-sage-600 hover:bg-sage-700
                           disabled:opacity-60 px-4 py-2.5 text-base font-medium text-white
                           transition-colors">
          {busy ? 'Sending…' : 'Send'}
        </button>
      </div>
    </Shell>
  );
}
